import { Box, Button, Flex, Text } from "@chakra-ui/react";
import React from "react";
import { RiCloseLine } from "react-icons/ri";
import { PersonNameLink } from "src/components/Navigation/PersonNameLink";
import { useGlossary } from "src/hooks/useGlossary";
import { useOrganization } from "src/hooks/useOrganization";
import { formatBirthDate } from "src/services/format";
import * as Url from "src/services/url";
import type { StudentEntry } from "./useStudentEntries";

interface StudentChipProps {
  entry: StudentEntry;
  // The parent being created, for a new student's "Child of" line.
  parentName: string;
  onRemove: (id: string) => void;
}

export function StudentChip({ entry, parentName, onRemove }: StudentChipProps) {
  const organization = useOrganization();
  const { glossaryTransform } = useGlossary();
  const fullName = [entry.firstName, entry.lastName].filter(Boolean).join(" ");
  const born = formatBirthDate(entry.birthDate);

  return (
    <Flex
      border="1px solid"
      borderColor="gray.200"
      borderRadius="lg"
      padding={4}
      gap={2.5}
      direction="row"
      align="flex-start"
    >
      <Box flex="1">
        <Text fontSize="md" fontWeight="700">
          {fullName}
        </Text>
        <Text fontSize="sm" color="gray.700">
          {born && <>Born {born}</>}
          {renderChildOf()}
        </Text>
        {entry.kind === "existing" && (
          <Text fontSize="sm" color="gray.700">
            ID: {entry.id}
          </Text>
        )}
      </Box>
      <Button
        type="button"
        variant="outline"
        colorScheme="gray"
        size="sm"
        p="0"
        onClick={() => onRemove(entry.id)}
        aria-label={glossaryTransform("Remove student")}
      >
        <RiCloseLine />
      </Button>
    </Flex>
  );

  function renderChildOf() {
    const separator = born ? " · " : "";

    if (entry.kind === "new") {
      if (!parentName) return null;
      return `${separator}Child of ${parentName}`;
    }

    if (entry.parents.length === 0) return null;
    return (
      <>
        {separator}Child of{" "}
        {entry.parents.map((parent, index) => (
          <React.Fragment key={parent.id}>
            {index > 0 && ", "}
            <PersonNameLink
              to={Url.OrgAdmin.Parents.edit(organization, parent.id)}
            >
              {[parent.firstName, parent.lastName].filter(Boolean).join(" ")}
            </PersonNameLink>
          </React.Fragment>
        ))}
      </>
    );
  }
}
